import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, type Category } from '../api/client';
import { useSession } from '../session/SessionContext';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { AccionIcono } from '../components/AccionIcono';
import { IconoActivar, IconoDesactivar, IconoEditar } from '../components/Iconos';

export function Catalogo() {
  const { t } = useTranslation();
  const { puede } = useSession();
  const administra = puede('category.manage');

  const [categorias, setCategorias] = useState<Category[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [nuevaAbierta, setNuevaAbierta] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [nombreEditado, setNombreEditado] = useState('');

  const cargar = useCallback(async () => {
    try {
      setCategorias(await api.categories(administra));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [administra]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function conAviso(accion: () => Promise<unknown>, aviso: string) {
    setOcupado(true);
    setError(null);
    try {
      await accion();
      setMensaje(aviso);
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  async function crear(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const formulario = evento.currentTarget;
    const datos = new FormData(formulario);
    const padre = String(datos.get('parentId') ?? '');

    await conAviso(async () => {
      await api.createCategory({
        code: String(datos.get('code')).toUpperCase(),
        name: String(datos.get('name')),
        ...(padre ? { parentId: padre } : {}),
      });
      formulario.reset();
      setNuevaAbierta(false);
    }, t('catalog.created'));
  }

  const guardarNombre = (categoria: Category) =>
    conAviso(async () => {
      await api.updateCategory(categoria.id, { name: nombreEditado });
      setEditando(null);
    }, t('catalog.renamed'));

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('catalog.title')}</h1>
        <p className="pagina__bajada">{t('catalog.subtitle')}</p>
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('catalog.taxonomy')}</h2>
          <span className="tarjeta__nota">{t('catalog.count', { count: categorias.length })}</span>
          {administra && (
            <button className="boton" type="button" onClick={() => setNuevaAbierta((v) => !v)}>
              {nuevaAbierta ? t('users.close') : t('catalog.newAction')}
            </button>
          )}
        </div>

        <div className="arbol">
          {categorias.map((categoria) => (
            <div
              className={`arbol__nodo${categoria.isActive === false ? ' arbol__nodo--inactiva' : ''}`}
              key={categoria.id}
              style={{ paddingLeft: `${0.55 + categoria.depth * 1.3}rem` }}
            >
              {editando === categoria.id ? (
                <form
                  className="arbol__edicion"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void guardarNombre(categoria);
                  }}
                >
                  <input
                    value={nombreEditado}
                    onChange={(e) => setNombreEditado(e.target.value)}
                    aria-label={t('catalog.name')}
                    autoFocus
                  />
                  <button className="boton boton--discreto" type="submit" disabled={ocupado}>
                    {t('catalog.save')}
                  </button>
                  <button
                    className="boton boton--fantasma"
                    type="button"
                    onClick={() => setEditando(null)}
                  >
                    {t('users.close')}
                  </button>
                </form>
              ) : (
                <>
                  {/* La taxonomía existe para llegar a los documentos: cada rama
                      lleva al catálogo ya filtrado por ella. */}
                  <Link className="arbol__enlace" to={`/documentos?categoryId=${categoria.id}`}>
                    {categoria.name}
                  </Link>
                  {/* La rama entera: una categoría madre suma los de sus hijas.
                      Cuando no todos son suyos se dice cuántos sí, para que el
                      número no parezca un error al abrirla. */}
                  <span className="tenue" style={{ fontSize: '.78rem' }}>
                    {t('catalog.documents', { count: categoria.documentCount })}
                    {categoria.ownDocumentCount !== categoria.documentCount &&
                      ` · ${t('catalog.ownDocuments', { count: categoria.ownDocumentCount })}`}
                  </span>
                  <span className="arbol__clave">{categoria.code}</span>
                  {categoria.isActive === false && (
                    <span className="pildora pildora--neutro">
                      <span className="pildora__punto" aria-hidden="true" />
                      {t('catalog.inactive')}
                    </span>
                  )}
                  {administra && (
                    <span className="arbol__acciones">
                      <AccionIcono
                        etiqueta={t('catalog.rename')}
                        icono={<IconoEditar />}
                        onClick={() => {
                          setEditando(categoria.id);
                          setNombreEditado(categoria.name);
                        }}
                      />
                      <AccionIcono
                        etiqueta={categoria.isActive === false ? t('catalog.reactivate') : t('catalog.deactivate')}
                        icono={categoria.isActive === false ? <IconoActivar /> : <IconoDesactivar />}
                        tono={categoria.isActive === false ? 'normal' : 'peligro'}
                        disabled={ocupado}
                        onClick={() =>
                          void conAviso(
                            () =>
                              api.updateCategory(categoria.id, {
                                isActive: categoria.isActive === false,
                              }),
                            categoria.isActive === false
                              ? t('catalog.reactivated')
                              : t('catalog.deactivated'),
                          )
                        }
                      />
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <p className="tarjeta__pie">{t('catalog.footnote')}</p>
      </section>

      {administra && nuevaAbierta && (
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('catalog.newTitle')}</h2>
          </div>
          <form onSubmit={crear} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="campos">
              <div className="campo">
                <label htmlFor="code">{t('catalog.code')}</label>
                <input id="code" name="code" required placeholder="MAN_CREDITO" />
                <span className="campo__ayuda">{t('catalog.codeHelp')}</span>
              </div>
              <div className="campo">
                <label htmlFor="name">{t('catalog.name')}</label>
                <input id="name" name="name" required minLength={3} />
              </div>
              <div className="campo">
                <label htmlFor="parentId">{t('catalog.parent')}</label>
                <select id="parentId" name="parentId" defaultValue="">
                  <option value="">{t('catalog.noParent')}</option>
                  {categorias
                    .filter((categoria) => categoria.isActive !== false)
                    .map((categoria) => (
                      <option key={categoria.id} value={categoria.id}>
                        {'— '.repeat(categoria.depth)}
                        {categoria.name}
                      </option>
                    ))}
                </select>
                <span className="campo__ayuda">{t('catalog.parentHelp')}</span>
              </div>
            </div>
            <div>
              <button className="boton" type="submit" disabled={ocupado}>
                {t('catalog.createAction')}
              </button>
            </div>
          </form>
        </section>
      )}
    </>
  );
}
